import React from 'react'
import {Box} from '@chakra-ui/react'
import {Picture, Video} from '_uiComponents'

export interface ProductImageProps {
    productImage: string
    hoverImage?: string
    videoSrc?: string
    isHorizontalTile?: boolean
    isDesktop?: boolean
    alt: string
    hasLazy?: boolean
    horizontalImage?: string
    horizontalImageAlt?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    styles: Record<string, any>
}

/**
 * @name ProductImage
 *
 * @description
 * Displays a product image, hover image, or video.
 *
 * @remark
 * The component can display an image or a video based on the provided props.
 * It also supports lazy loading and different styling options for horizontal tiles and desktop views.
 *
 * @param {ProductImageProps} props - The props for the component.
 * @param {string} props.productImage - The main product image URL.
 * @param {string} props.hoverImage - The image URL to display on hover.
 * @param {string} props.videoSrc - The video URL to display instead of an image.
 * @param {boolean} props.isHorizontalTile - Boolean indicating if the layout is a horizontal tile.
 * @param {boolean} props.isDesktop - Boolean indicating if the component is being rendered on a desktop.
 * @param {string} props.alt - The alt text for the image.
 * @param {boolean} props.hasLazy - Boolean indicating if the image should be lazily loaded.
 * @param {string} props.horizontalImage - The image URL for the horizontal layout.
 * @param {string} props.horizontalImageAlt - The alt text for the horizontal image.
 * @param {Record<string, any>} props.styles - Custom styles for the component.
 *
 * @returns Rendered `ProductImage` component.
 */
export const ProductImage: React.FC<ProductImageProps> = ({
    productImage,
    hoverImage,
    videoSrc,
    isHorizontalTile = false,
    isDesktop = false,
    alt,
    hasLazy = false,
    horizontalImage,
    horizontalImageAlt,
    styles,
}) => {
    return (
        <Box {...styles.imageWrapper}>
            {videoSrc ? (
                <Video videoxs={videoSrc} autoplay loop isTileImage position="absolute" objectFit="cover" alt={alt} />
            ) : (
                <>
                    <Picture
                        imagexs={isHorizontalTile ? horizontalImage : productImage}
                        isTileImage
                        isTileHorizontal={isHorizontalTile}
                        hasLazy={hasLazy}
                        position="absolute"
                        alt={isHorizontalTile ? horizontalImageAlt : alt}
                        objectFit="cover"
                    />
                    {hoverImage && isDesktop && !isHorizontalTile && (
                        <Box {...styles.hoverImage}>
                            <Picture imagexs={hoverImage} isTileImage position="absolute" alt={alt} objectFit="cover" />
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}
